<template>
	<div>
		<SelectionModal
			:title="$t('chat_directmessage_start_title')"
			:confirmText="$t('common_button_start')"
			:large="false"
			:confirmProps="{ loading: saving }"
			:items="users"
			:columns="columns"
			:selectedItemsById="selectedUsersById"
			:sortItems="sortUsersByFullname"
			:loading="fetchingUsers"
			:loadingMore="loadingMore"
			:startingCount="startingCount"
			:maxLimit="maxLimit"
			skipDiscard
			allowEmptySelection
			hideFooterContent
			class="create-dm"
			@selectItem="onSelectItem"
			@selection="onSelectionChange"
			@load-more="loadMore"
			@confirm="onClickConfirm"
			@cancel="onClose"
			@prevented="onPrevented"
			ref="modal"
			:class="{ 'has-selected-users': hasSelected }"
		>
			<template #cell="{ data }">
				<UserCell :key="data.id" :user="data" hideTag hideContact />
			</template>
			<template #filters>
				<div class="filters-container">
					<TransitionGroup
						class="selected-users"
						tag="div"
						:class="{ expanded: hasSelected }"
						name="selected-user"
					>
						<div
							class="description"
							v-if="!hasSelected"
							key="description"
							v-text="
								$tc('chat_directmessage_start_limit_count.variable', maxLimit, {
									Count: maxLimit,
								})
							"
						/>
						<Removable
							v-for="user in selectedUsersInSelectedOrder"
							:key="user.id"
							@remove="onRemoveUser(user)"
						>
							<div class="selected-user">
								<ProfilePic :user="user" size="40px" />
								<div class="selected-user-name" v-text="user.fullname" />
							</div>
						</Removable>
					</TransitionGroup>
					<div class="filters">
						<Input
							:placeholder="$t('admin_people_search_action')"
							class="search"
							v-model="query"
							ref="searchInput"
						/>
						<OrgUserFilter @change="updateFilter" />
					</div>
				</div>
			</template>
		</SelectionModal>

		<StandardModal
			v-if="failedDMMessage"
			open
			small
			:title="$t('common_no_permission')"
			:confirmText="$t('common_button_okay')"
			hideCancel
			@confirm="failedDMMessage = null"
			@cancel="failedDMMessage = null"
			class="extra-modal"
		>
			<div class="extra-modal-wrapper">
				<PaperAirplaneIcon halo />
				{{ failedDMMessage }}
			</div>
		</StandardModal>

		<StandardModal
			v-if="selfDMConfirm"
			open
			small
			:title="$t('chat_directmessage_start_self_alert_title')"
			:confirmText="$t('common_button_start')"
			:confirmProps="{ loading: saving }"
			@confirm="createDMChat"
			@cancel="selfDMConfirm = false"
			class="extra-modal"
		>
			<div class="extra-modal-wrapper">
				<CommentsIcon halo type="regular" />
				{{ $t('chat_directmessage_start_self_alert_description') }}
			</div>
		</StandardModal>

		<StandardModal
			v-if="showMaxAlert"
			open
			small
			:title="$t('chat_directmessage_limit_alert_title')"
			:confirmText="$t('common_button_okay')"
			hideCancel
			@confirm="showMaxAlert = false"
			@cancel="showMaxAlert = false"
			class="extra-modal"
		>
			<div class="extra-modal-wrapper">
				<CommentsIcon halo type="regular" />
				<div
					@click="openGroupModal()"
					v-safe-html="
						$t(
							canCreateGroup
								? 'chat_directmessage_start_limit_full_with_group_creation_link'
								: 'chat_directmessage_start_limit_full'
						)
					"
				/>
			</div>
		</StandardModal>
	</div>
</template>

<script setup>
import { ref, onMounted, useTemplateRef, nextTick, watch, computed } from 'vue'
import { useRouter } from 'vue-router'
import { getMyUserId, setChat, sortUsersByFullname } from '@/store'
import {
	Input,
	ProfilePic,
	StandardModal,
	Removable,
} from '@/components/common'
import {
	get,
	SelectionModal,
	UserCell,
} from '@/components/OrgManagement/common'
import OrgUserFilter from '@/components/CreateGroup/OrgUserFilter'
import { MAX_DM_MEMBERS } from '@/helpers/constants'
import debounce from 'debounce'
import { CommentsIcon, PaperAirplaneIcon } from '@/icons'
import { createDM } from '@/api/chat'
import { CREATE_GROUP, CREATE_MULTIDM, orgCan } from '@/helpers/permissions'

const PAGE_SIZE = 30
const emit = defineEmits(['close', 'openGroup'])
const modal = useTemplateRef('modal')
const searchInput = useTemplateRef('searchInput')
const router = useRouter()

const filter = ref('')
const fetchingUsers = ref(false)
const loadingMore = ref(false)
const saving = ref(false)
const selfDMConfirm = ref(false)
const showMaxAlert = ref(false)
const users = ref([])
const columns = ref([{ field: 'user' }])
const query = ref('')
const startingCount = ref(0)
const selectedUsersById = ref({})
const selectedUsersInSelectedOrder = ref([])
const failedDMMessage = ref(null)
let apiResponse = null

const canCreateGroup = ref(false)
const canCreateMultiDM = ref(false)

const fetchUsersDebounced = debounce(fetchUsers, 250)

const userIds = computed(() =>
	Object.values(selectedUsersById.value)
		.filter((u) => u.selected)
		.map((u) => u.item.id)
)

const userCount = computed(() => userIds.value.length)
const hasSelected = computed(
	() => selectedUsersInSelectedOrder.value.length > 0
)
const maxLimit = computed(() =>
	canCreateMultiDM.value ? MAX_DM_MEMBERS - 1 : 1
)
const isAtMaxLimit = computed(() => userCount.value >= maxLimit.value)

onMounted(async () => {
	searchInput.value?.focus()
	canCreateGroup.value = orgCan(CREATE_GROUP)
	canCreateMultiDM.value = orgCan(CREATE_MULTIDM)
	fetchUsers()
})

watch(query, () => {
	fetchingUsers.value = true
	fetchUsersDebounced()
})

async function updateFilter(value) {
	filter.value = value
	await fetchUsers()
}

function onSelectItem(item, selected) {
	if (selected) {
		selectedUsersInSelectedOrder.value.push(item)
	} else {
		selectedUsersInSelectedOrder.value = selectedUsersInSelectedOrder.value.filter(
			(u) => u.id !== item.id
		)
	}
}

function onSelectionChange(selectedItems) {
	selectedUsersById.value = selectedItems
	setTimeout(() => {
		query.value = ''
		searchInput.value?.focus()
	}, 100)
}

function onRemoveUser(user) {
	// toggling the selection state will remove the user from the list
	modal.value.onSelect(user)
}

function onPrevented() {
	if (isAtMaxLimit.value) {
		showMaxAlert.value = true
	}
}

function onClose() {
	emit('close')
}

function openGroupModal() {
	if (!canCreateGroup.value) return
	const users = Object.values(selectedUsersById.value)
		.filter((u) => u.selected)
		.map((u) => u.item)
	emit('openGroup', users)
}

function onClickConfirm() {
	if (userCount.value === 0) {
		selfDMConfirm.value = true
	} else {
		createDMChat()
	}
}

async function createDMChat() {
	try {
		const myId = getMyUserId()
		saving.value = true
		const ids = [myId]
		if (userIds.value.length > 0) {
			ids.push(...userIds.value)
		}
		const chat = await createDM(ids)
		setChat(chat)
		setTimeout(() => {
			router.push('/recents/' + chat.id)
			onClose()
		}, 250)
	} catch (e) {
		console.error(e)
		const [{ members, message }] = e.response?.data?.errors?.members || [{}]
		if (members?.length > 0) {
			const failedNames = members
				.map((id) => selectedUsersById.value[id]?.item)
				.filter(Boolean)
				.map((user) => user.full_name || user.fullname)
				.join(', ')
			failedDMMessage.value = [message, failedNames].join(' ')
		} else {
			modal.value.displayAlert(e)
		}
	} finally {
		saving.value = false
	}
}

async function loadMore() {
	if (!apiResponse?.next || loadingMore.value) return
	try {
		loadingMore.value = true
		apiResponse = await apiResponse.next()
		users.value = users.value.concat(apiResponse.data)
	} catch (e) {
		console.error(e)
	}

	await nextTick()
	loadingMore.value = false
}

async function fetchUsers() {
	const myId = getMyUserId()
	fetchingUsers.value = true
	const params = { 'page[size]': PAGE_SIZE }
	if (filter.value === 'all') {
		params['filter[relation]'] = 'all'
	} else if (filter.value === 'connections') {
		params['filter[relation]'] = 'connections'
	} else if (Number.isInteger(filter.value)) {
		params['filter[bubble_ids][]'] = filter.value
	}
	if (query.value) {
		params['filter[query]'] = query.value
	}

	apiResponse = await get('/users/search', params)
	const filteredUsers = apiResponse.data.filter(
		(user) => user.id !== myId && user.role !== 'support'
	)
	users.value = filteredUsers

	fetchingUsers.value = false
	modal.value.$refs.mainTable.scrollToTop()
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.filters-container {
	@include flex-column(8px);
	.filters {
		@include flex(16px);
	}

	.selected-users {
		--animation-duration: 0.3s;
		--width: 48px;
		--height: 68px;
		position: relative;
		@include flex(12px);
		align-items: start;
		flex-wrap: nowrap;
		overflow-x: auto;
		overflow-y: hidden;
		height: 30px;
		padding-top: 10px;
		transition: height var(--animation-duration) ease-in-out;

		&.expanded {
			height: var(--height);
			.description {
				opacity: 0;
			}
		}

		.description {
			// absolute to avoid layout shift when the selected users animate in
			position: absolute;
			top: 8px;
			left: 0;
			@include fontFamily(400);
			color: var(--neutral-600);
			transition: opacity var(--animation-duration) ease-in-out;
		}

		.pronto-removable {
			width: var(--width);
			flex-shrink: 0;
			opacity: 1;

			&.selected-user-enter-active,
			&.selected-user-leave-active {
				transition: all var(--animation-duration) ease-in-out;
			}
			&.selected-user-enter-from,
			&.selected-user-leave-to {
				width: 0;
				opacity: 0;
			}
		}

		.selected-user {
			height: var(--height);
			width: var(--width);
			overflow-x: auto;
			overflow-y: hidden;
			position: relative;
			@include flex-column-center;
			align-items: start;

			.profile-pic-avatar {
				flex-shrink: 0;
			}

			.selected-user-name {
				@include fontFamily(400);
				color: var(--neutral-800);
				font-size: 10px;
				@include ellipsis;
				max-width: calc(var(--width) - 8px);
				margin-top: 4px;
			}
		}
	}
}

.extra-modal {
	.extra-modal-wrapper {
		@include flex-column-center;
		max-width: 400px;
		margin: 24px auto;
		text-align: center;
		.pronto-icon {
			color: var(--blue-300);
			font-size: 50px;
			margin-bottom: 24px;
		}
		:deep(a) {
			color: var(--blue-300);
			text-decoration: underline;
			cursor: pointer;
		}
	}
}
</style>
<style lang="scss">
.create-dm.selection-modal {
	.filtering {
		padding-top: 0;
	}
	&.has-selected-users {
		.modal-body {
			overflow-y: hidden;
			.pronto-table {
				--table-height: 405px !important;
			}
		}
	}
}
</style>
